import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { useAnimations } from '../../hooks/useAnimations';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Calendar, 
  Clock, 
  Dumbbell,
  Search,
  Filter,
  AlertCircle,
  CheckCircle,
  X
} from 'lucide-react';
import { createApiClient } from '../../services/api';

// Componente per la card della scheda
const SchedaCard = ({ scheda, onDelete, onViewDetails, onEdit }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const { getAnimationClasses, getInteractionClasses } = useAnimations();

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-200 hover:shadow-md ${getAnimationClasses('fadeIn')}`}>
      {/* Header della card */}
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {scheda.nome}
            </h3>
            {scheda.descrizione && (
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                {scheda.descrizione}
              </p>
            )}
            
            <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center">
                <Dumbbell className="h-4 w-4 mr-1" />
                {scheda.num_esercizi || 0} {t('viewWorkoutsPage.card.exercises')}
              </div>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                {scheda.data_creazione ? new Date(scheda.data_creazione).toLocaleDateString('it-IT') : t('viewWorkoutsPage.card.dateNotAvailable')}
              </div>
            </div>
          </div>
          
        </div>
      </div>

      {/* Azioni rapide */}
      <div className="px-6 pb-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onEdit(scheda.id)}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            <Edit className="h-4 w-4 mr-2 inline" />
            {t('viewWorkoutsPage.card.edit')}
          </button>
          
          <button
            onClick={() => onViewDetails(scheda.id)}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            <Eye className="h-4 w-4" />
          </button>
          
          <button
            onClick={() => onDelete(scheda.id)}
            className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

    </div>
  );
};

// Componente per i dettagli della scheda
const SchedaDetailsModal = ({ scheda, onClose }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const { getAnimationClasses } = useAnimations();

  if (!scheda) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto ${getAnimationClasses('fadeIn')}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{scheda.nome}</h2>
              {scheda.descrizione && (
                <p className="text-gray-600 dark:text-gray-400 mt-1">{scheda.descrizione}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Statistiche */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center">
                  <Dumbbell className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('viewWorkoutsPage.details.exercises')}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{scheda.num_esercizi || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center">
                  <Clock className="h-8 w-8 text-green-600 dark:text-green-400" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('viewWorkoutsPage.details.estimatedTime')}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {scheda.num_esercizi ? Math.round(scheda.num_esercizi * 5) : 0} min
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center">
                  <Calendar className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('viewWorkoutsPage.details.created')}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {scheda.data_creazione ? new Date(scheda.data_creazione).toLocaleDateString('it-IT') : t('viewWorkoutsPage.card.dateNotAvailable')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Lista esercizi */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('viewWorkoutsPage.details.exercises')}</h3>
              {scheda.esercizi && scheda.esercizi.length > 0 ? (
                <div className="space-y-3">
                  {scheda.esercizi.map((esercizio, index) => (
                    <div key={esercizio.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <span className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                          {index + 1}
                        </span>
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">{esercizio.nome}</h4>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{esercizio.gruppo_muscolare}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {esercizio.serie} x {esercizio.ripetizioni}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          @ {esercizio.peso}kg
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                  {t('viewWorkoutsPage.details.noExercisesAvailable')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function ImprovedVisualizzaSchede() {
  const { currentUser, token } = useAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const { getAnimationClasses, getInteractionClasses } = useAnimations();
  const navigate = useNavigate();
  const api = createApiClient(token);

  // Stati
  const [schede, setSchede] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedScheda, setSelectedScheda] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Stati per messaggi locali (no NotificationContext per standalone)
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // Carica schede
  const fetchSchede = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/schede_standalone.php?user_id=${currentUser.user_id}`);
      const data = response.data;
      if (data.success) {
        setSchede(data.schede);
      } else {
        setMessage(t('viewWorkoutsPage.errors.loadError'));
        setMessageType('error');
      }
    } catch (error) {
      console.error('Errore nel caricamento schede:', error);
      setMessage('Errore nel caricamento delle schede');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  }, [api, currentUser.user_id]);

  useEffect(() => {
    if (currentUser?.user_id) {
      fetchSchede();
    }
  }, [currentUser?.user_id]);

  // Gestione eliminazione
  const handleDelete = async (schedaId) => {
    if (!window.confirm(t('viewWorkoutsPage.confirmDelete'))) return;

    setLoading(true);
    try {
      // L'API si aspetta un POST con action=delete in formato form data
      const formData = new FormData();
      formData.append('action', 'delete');
      formData.append('scheda_id', schedaId);
      
      const response = await api.post('/schede_standalone.php', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      const data = response.data;

      if (data.success) {
        setMessage(t('viewWorkoutsPage.deleteSuccess'));
        setMessageType('success');
        setSchede(prev => prev.filter(s => s.id !== schedaId));
      } else {
        setMessage(data.message || t('viewWorkoutsPage.errors.deleteError'));
        setMessageType('error');
      }
    } catch (error) {
      console.error('Errore nell\'eliminazione scheda:', error);
      setMessage('Errore nell\'eliminazione della scheda');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  // Gestione visualizzazione dettagli
  const handleViewDetails = async (schedaId) => {
    try {
      setLoading(true);
      // Carica i dati completi della scheda
      // Carica i dati completi della scheda (solo con scheda_id)
      const response = await api.get(`/schede_standalone.php?scheda_id=${schedaId}`);
      const data = response.data;
      
      if (data.success) {
        // Crea un oggetto scheda completo con i dati ricevuti
        const schedaCompleta = {
          id: schedaId,
          nome: data.nome || 'Scheda senza nome',
          descrizione: data.descrizione || '',
          esercizi: data.esercizi || [],
          num_esercizi: data.esercizi ? data.esercizi.length : 0,
          data_creazione: data.data_creazione || new Date().toISOString()
        };
        setSelectedScheda(schedaCompleta);
      } else {
        // Fallback: usa i dati dalla lista locale
        const scheda = schede.find(s => s.id === schedaId);
        setSelectedScheda(scheda);
        setMessage(t('viewWorkoutsPage.errors.loadError'));
        setMessageType('error');
      }
    } catch (error) {
      // Fallback: usa i dati dalla lista locale
      const scheda = schede.find(s => s.id === schedaId);
      setSelectedScheda(scheda);
      console.error('Errore nel caricamento dettagli scheda:', error);
      setMessage('Errore nel caricamento dei dettagli della scheda');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  // Gestione modifica
  const handleEdit = (schedaId) => {
    navigate(`/standalone/modifica-scheda/${schedaId}`);
  };



  // Filtri e ordinamento
  const filteredAndSortedSchede = schede
    .filter(scheda => 
      scheda.nome.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (scheda.descrizione && scheda.descrizione.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    .sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (sortBy === 'created_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  return (
    <SidebarLayout>
      <div className="w-[95%] mx-auto px-4 py-6">
        {/* Header */}
        <div className={`mb-6 ${getAnimationClasses('fadeIn')}`}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t('viewWorkoutsPage.title')}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {t('viewWorkoutsPage.description')}
              </p>
            </div>
            
            <Link
              to="/standalone/crea-scheda"
              className={`px-6 py-3 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors ${getInteractionClasses()}`}
            >
              <Plus className="h-5 w-5 mr-2 inline" />
              {t('viewWorkoutsPage.createNew')}
            </Link>
          </div>
        </div>

        {/* Messaggi di feedback */}
        {message && (
          <div className={`mb-4 p-4 rounded-lg ${
            messageType === 'success' 
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800' 
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
          }`}>
            <p>{message}</p>
          </div>
        )}

        {/* Filtri e ricerca */}
        <div className={`mb-6 ${getAnimationClasses('slideInDown')}`}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('viewWorkoutsPage.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="created_at">{t('viewWorkoutsPage.sortBy.createdAt')}</option>
                <option value="nome">{t('viewWorkoutsPage.sortBy.name')}</option>
                <option value="num_esercizi">{t('viewWorkoutsPage.sortBy.numExercises')}</option>
              </select>
              
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="desc">{t('viewWorkoutsPage.sortOrder.desc')}</option>
                <option value="asc">{t('viewWorkoutsPage.sortOrder.asc')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Lista schede */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-400">{t('viewWorkoutsPage.loading')}</span>
          </div>
        ) : filteredAndSortedSchede.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAndSortedSchede.map((scheda) => (
              <SchedaCard
                key={scheda.id}
                scheda={scheda}
                onDelete={handleDelete}
                onViewDetails={handleViewDetails}
                onEdit={handleEdit}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Dumbbell className="h-16 w-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {t('viewWorkoutsPage.noPlansFound')}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              {searchQuery ? t('viewWorkoutsPage.noPlansDescription') : t('viewWorkoutsPage.noPlansEmpty')}
            </p>
            {!searchQuery && (
              <Link
                to="/standalone/crea-scheda"
                className={`px-6 py-3 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors ${getInteractionClasses()}`}
              >
                <Plus className="h-5 w-5 mr-2 inline" />
                {t('viewWorkoutsPage.createFirstPlan')}
              </Link>
            )}
          </div>
        )}

        {/* Modale dettagli */}
        {selectedScheda && (
          <SchedaDetailsModal
            scheda={selectedScheda}
            onClose={() => setSelectedScheda(null)}
          />
        )}
      </div>
    </SidebarLayout>
  );
}
